import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

import type { Tables } from "@/integrations/supabase/types";

type ProviderProfile = Tables<"provider_profiles">;

type CreateProviderData = Pick<ProviderProfile, "company_name" | "contact_name"> &
  Partial<Pick<ProviderProfile, "contact_role" | "region" | "remote_supported" | "sla_response_hours" | "weekly_lead_limit">>;

export const useProvider = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["provider-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from("provider_profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const createProfile = useMutation({
    mutationFn: async (data: CreateProviderData) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { data: newProfile, error } = await supabase
        .from("provider_profiles")
        .insert({
          user_id: user.id,
          ...data,
        })
        .select()
        .single();

      if (error) throw error;
      return newProfile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provider-profile"] });
    },
  });

  const updateProfile = useMutation({
    mutationFn: async (data: Partial<Omit<ProviderProfile, "id" | "user_id" | "created_at">>) => {
      if (!profile?.id) throw new Error("No profile found");

      const { data: updated, error } = await supabase
        .from("provider_profiles")
        .update(data)
        .eq("id", profile.id)
        .select()
        .single();

      if (error) throw error;
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provider-profile"] });
    },
  });

  return {
    profile,
    isLoadingProfile,
    createProfile,
    updateProfile,
    hasCompletedOnboarding: profile?.onboarding_completed ?? false,
  };
};
